import type {
  EmailTemplateBlock,
  EmailTemplateFinding,
  EmailTemplateSeverity,
} from "@/types/emailTemplates";

/**
 * Client mirror of `EmailTemplateValidator` (Core/Emails).
 *
 * 🔴 **Why it is duplicated rather than fetched.** The editor BLOCKS SUBMIT on a
 * hard error, so the author's text never leaves the textarea and nothing is lost.
 * The server refuses the same edit for defence in depth. Round-tripping to learn
 * about a typo would mean either the text leaves before it is checked, or every
 * keystroke costs a request — and the sibling Document Language Editor's flow
 * (store the failing draft, gate only the publish) is exactly what this module
 * deliberately does not do.
 *
 * 🔴 **Keep in lockstep with the C# validator.** These are the same rules in the
 * same order, and where a message differs the author gets two accounts of one
 * failure. The constants below are the ones that drift most easily.
 */

/** The one `{{CamelCase}}` grammar. Whitespace-INTOLERANT and deliberately so —
 *  `{{ Name }}` must NOT match, so it can be reported rather than rendering as
 *  literal braces to a counterparty. Mirrors `MergeTokenGrammar.Pattern`. */
const TOKEN = /\{\{([A-Za-z][A-Za-z0-9]*)\}\}/g;

/** Anything brace-wrapped, so a near-miss can be caught and named. Mirrors
 *  `EmailTemplateValidator.BraceWrapped`. */
const BRACE_WRAPPED = /\{\{([^{}]*)\}\}/g;

/** `MergeTokenGrammar.Description`, verbatim. An author who typed `{{ Name }}`
 *  needs to be told about the spaces, not merely that it was rejected. */
export const TOKEN_GRAMMAR_DESCRIPTION =
  "{{TokenName}} — letters and digits only, no spaces inside the braces";

/** `EmailTemplateValidator.MaxSubjectLength`. */
export const MAX_SUBJECT_LENGTH = 200;

/** `EmailTemplateValidator.MaxBodyLength`. */
export const MAX_BODY_LENGTH = 4000;

/** `<strong>`/`</strong>`, case-insensitive, no attributes — the ONLY tag a body
 *  block may carry. Mirrors `EmailTemplateValidator.StrongTag`. */
const STRONG_TAG = /<\/?strong>/gi;

/** Distinct token names in first-seen order. Mirrors `MergeTokenGrammar.ExtractNames`. */
export function extractTokenNames(text: string): string[] {
  const seen: string[] = [];
  for (const m of text.matchAll(TOKEN)) {
    if (!seen.includes(m[1])) seen.push(m[1]);
  }
  return seen;
}

function finding(
  blockKey: string,
  message: string,
  severity: EmailTemplateSeverity,
): EmailTemplateFinding {
  return { blockKey, message, severity };
}

/**
 * Validate one proposed block edit.
 *
 * @param block      the catalog block being edited (owns the kind and the token
 *                   whitelist — a plain-text VARIANT shares both with its base
 *                   block, which is why the base block is what gets passed).
 * @param text       the proposed copy.
 * @param blockKey   the key findings are tagged with — the storage key
 *                   (`{block}:text`) for a variant, so the editor can attach the
 *                   message to the field the author is typing in.
 */
export function validateEmailTemplateBlock(
  block: Pick<EmailTemplateBlock, "kind" | "allowedTokens" | "requiredTokens">,
  text: string,
  blockKey: string,
): EmailTemplateFinding[] {
  const value = text ?? "";
  const findings: EmailTemplateFinding[] = [];
  const isSubject = block.kind === "Subject";

  // (2) Empty. A blank subject sends an unopenable email; a blank body block
  // sends a hole. ERROR on subject, WARNING on body (an author may be clearing a
  // block they will refill).
  if (value.trim() === "") {
    if (isSubject) {
      return [finding(blockKey, "The subject line can't be empty.", "Error")];
    }
    findings.push(
      finding(
        blockKey,
        "This block is empty — it will render as nothing in the sent email.",
        "Warning",
      ),
    );
  }

  // (3) Token whitelist. ERROR — an unknown token throws at render time, so
  // letting it publish would take the email down rather than degrade it.
  const used = extractTokenNames(value);
  for (const name of used) {
    if (!block.allowedTokens.includes(name)) {
      const allowed =
        block.allowedTokens.length === 0
          ? "this block takes no placeholders"
          : "available here: " + block.allowedTokens.map((t) => `{{${t}}}`).join(", ");
      findings.push(finding(blockKey, `Unknown placeholder {{${name}}} — ${allowed}.`, "Error"));
    }
  }

  // (3b) Malformed placeholder. The grammar is whitespace-intolerant, so
  // `{{ Name }}` matched nothing above and would otherwise publish silently and
  // render as literal braces. Name the grammar — this is the message that stops
  // an author guessing.
  const seenMalformed = new Set<string>();
  for (const m of value.matchAll(BRACE_WRAPPED)) {
    const whole = m[0];
    // Matched by the real grammar => well-formed, already handled above.
    if (new RegExp(`^${TOKEN.source}$`).test(whole)) continue;
    if (seenMalformed.has(whole)) continue;
    seenMalformed.add(whole);
    findings.push(
      finding(
        blockKey,
        `"${whole}" isn't a valid placeholder. The format is ${TOKEN_GRAMMAR_DESCRIPTION}.`,
        "Error",
      ),
    );
  }

  // (4) Required tokens. ERROR — the copy would go stale by dropping the fact it
  // exists to carry.
  for (const required of block.requiredTokens) {
    if (!used.includes(required)) {
      findings.push(
        finding(blockKey, `This block must include {{${required}}} so it can't go stale.`, "Error"),
      );
    }
  }

  // (5) Length + (6) shape, per kind.
  if (isSubject) {
    if (value.length > MAX_SUBJECT_LENGTH) {
      findings.push(
        finding(
          blockKey,
          `The subject is ${value.length} characters — the limit is ${MAX_SUBJECT_LENGTH}.`,
          "Error",
        ),
      );
    }
    if (value.includes("\n") || value.includes("\r")) {
      findings.push(finding(blockKey, "A subject line can't contain line breaks.", "Error"));
    }
    if (value.includes("<") || value.includes(">")) {
      findings.push(
        finding(blockKey, "A subject line is plain text — it can't contain HTML.", "Error"),
      );
    }
  } else {
    if (value.length > MAX_BODY_LENGTH) {
      findings.push(
        finding(
          blockKey,
          `This block is ${value.length} characters — the limit is ${MAX_BODY_LENGTH}.`,
          "Error",
        ),
      );
    }
    const withoutStrong = value.replace(STRONG_TAG, "");
    if (withoutStrong.includes("<") || withoutStrong.includes(">")) {
      findings.push(
        finding(
          blockKey,
          "Only <strong>…</strong> emphasis is allowed; other HTML isn't permitted here.",
          "Error",
        ),
      );
    }
  }

  return findings;
}

/** True when any finding blocks the write. Warnings never do. */
export function hasHardError(findings: readonly EmailTemplateFinding[]): boolean {
  return findings.some((f) => f.severity === "Error");
}
